import Facade from '@/Facade'
import { Model } from '@/model/Model'
import { capFeatureWinAmount } from '@/states/round/MaxWinCap'
import { toMajorUnits } from '@/utils/CurrencyUnits'

export const isLastFreeSpinRound = (model: Model): boolean => {
    const amountLeft = Number(model.spinData?.freebet?.amount_left ?? -1)
    return Boolean(model.roundState.isFreeSpins) && Number.isFinite(amountLeft) && amountLeft === 0
}

/** True only when FS is actually finishing (summary next) — not while H&W was triggered on the last FS. */
export const isFreeSpinsFeatureCompleteRound = (model: Model): boolean => {
    if (!isLastFreeSpinRound(model)) {
        return false
    }

    if (Boolean(model.spinState?.isHoldAndWinActive)) {
        return false
    }

    const nextRoundType = String(model.spinData?.next_round_type || '').toLowerCase()
    if (nextRoundType === 'respin' || nextRoundType === 'respins') {
        return false
    }

    const respinsLeft = Number(model.spinData?.respins_left ?? 0)
    if (Number.isFinite(respinsLeft) && respinsLeft > 0) {
        return false
    }

    if ((Facade as any).holdAndWinCollectInProgress || (Facade as any).holdAndWinPendingBaseTransition) {
        return false
    }

    return true
}

/** Prefer server `total_fs_win_amount` (never single-spin `win_amount`) for end-of-FS commendation. */
export const getFreeSpinsFinalWinAmount = (model: Model): number => {
    const spinData = model.spinData as any
    const freebet = spinData?.freebet
    const currentRound = spinData?.current_round
    const nextRound = spinData?.next_round

    // Explicit total_fs_* first — freebet.total_win can be polluted by promo/win fallbacks.
    const candidates = [
        currentRound?.total_fs_win_amount,
        spinData?.total_fs_win_amount,
        nextRound?.total_fs_win_amount,
        currentRound?.total_fs_win_amount_currency,
        spinData?.total_fs_win_amount_currency,
        nextRound?.total_fs_win_amount_currency,
        freebet?.total_coin_win,
        freebet?.total_win,
        model.winCounter?.totalWin,
    ]

    for (const candidate of candidates) {
        const value = Number(candidate)
        if (Number.isFinite(value) && value > 0) {
            return capFeatureWinAmount(model, value)
        }
    }

    return 0
}

/**
 * Part of the server FS total the Win field has not shown yet, in major units.
 *
 * The last free spin already carries the ladder prize inside the server balance, while the Win
 * field still holds only the payline wins presented so far. That difference must stay out of the
 * wallet until FreeSpinsSummaryState credits it together with the summary popup. Non-final spins
 * return 0, so the wallet keeps tracking the server balance for the rest of the feature.
 */
export const getUncreditedFreeSpinsWinMajorUnits = (model: Model, creditedMajorUnits: number): number => {
    if (!isFreeSpinsFeatureCompleteRound(model)) {
        return 0
    }

    const totalMajorUnits = toMajorUnits(getFreeSpinsFinalWinAmount(model))
    const credited = Number(creditedMajorUnits)

    if (!Number.isFinite(totalMajorUnits) || !Number.isFinite(credited)) {
        return 0
    }

    return Math.max(0, Number((totalMajorUnits - credited).toFixed(2)))
}

export const isFreeSpinsParentType = (value: unknown): boolean => {
    const type = String(value || '').toLowerCase()
    return type === 'freespins' || type === 'freespin'
}

export const isHoldAndWinInsideFreeSpins = (model: Model): boolean => {
    const spinData = model.spinData as any
    const currentParentType = spinData?.current_round?.parent_type
    const nextParentType = spinData?.next_round?.parent_type
    const spinParentType = spinData?.parent_type

    return (
        Boolean(model.roundState?.isFreeSpins) ||
        isFreeSpinsParentType(currentParentType) ||
        isFreeSpinsParentType(nextParentType) ||
        isFreeSpinsParentType(spinParentType)
    )
}
